import { EEGRegime, OptimizationGroupParametersType, TargetRatedPowerGroupType } from '@e2m-customer-portal/domains';
import { useLocale, useStartOfWeek } from '@e2m-customer-portal/hooks';
import { Nullable, mustBeGreaterThan } from '@e2m-customer-portal/shared';
import { DateTimePicker, InfoTooltip, NumberInput, TextInput, ValidatedContainer } from '@e2m-customer-portal/ui';
import { FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import Button from '@mui/material/Button';
import clsx from 'clsx';
import { parseISO } from 'date-fns';
import { Dispatch, SetStateAction, memo, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

export interface TargetRatedPowerGroupProps {
  data: TargetRatedPowerGroupType;
  targetRatedPowerGroups: TargetRatedPowerGroupType[];
  setUpdatedData: Dispatch<SetStateAction<OptimizationGroupParametersType>>;
  setTargetRatedPowerGroups: Dispatch<SetStateAction<TargetRatedPowerGroupType[] | undefined>>;
  readOnly: (parameter?: string) => boolean;
}

function TargetRatedPowerGroupMemo({
  data,
  targetRatedPowerGroups,
  setTargetRatedPowerGroups,
  readOnly,
}: Readonly<TargetRatedPowerGroupProps>) {
  const [t] = useTranslation();
  const [tv] = useTranslation('validation');
  const [isValid, setIsValid] = useState(false);
  const locale = useLocale();
  const startOfWeek = useStartOfWeek();

  const [updatedTargetRatedPowerGroup, setUpdatedTargetRatedPowerGroup] = useState<TargetRatedPowerGroupType>(data);

  useEffect(() => {
    if (isValid) {
      const target_rated_power_groups = targetRatedPowerGroups.map((item) =>
        item.identifier === updatedTargetRatedPowerGroup.identifier ? updatedTargetRatedPowerGroup : item,
      );
      target_rated_power_groups && setTargetRatedPowerGroups(target_rated_power_groups);
    }
  }, [updatedTargetRatedPowerGroup]);

  useEffect(() => {
    setUpdatedTargetRatedPowerGroup(data);
  }, [data]);

  return (
    <div className="relative h-fit px-4 py-6 grid grid-cols-1 xl:grid-cols-2 gap-x-2 gap-y-4 bg-white rounded-md border-solid border border-divider">
      <ValidatedContainer validate="on" onValidated={setIsValid}>
        <Button
          color="error"
          className="absolute -right-4 -top-4 bg-white h-8 w-8 min-w-8 shrink-0 grow-0 items-center justify-center rounded-full hover:bg-error-500 hover:text-white z-10"
          variant="outlined"
          disabled={readOnly('target_rated_power_groups')}
          onClick={() => {
            setTargetRatedPowerGroups([
              ...targetRatedPowerGroups.filter((group) => group.identifier !== updatedTargetRatedPowerGroup.identifier),
            ]);
          }}
        >
          <span className="-translate-y-0.5">x</span>
        </Button>
        {updatedTargetRatedPowerGroup.name !== undefined && (
          <div className="xl:col-span-2">
            <TextInput
              value={updatedTargetRatedPowerGroup.name}
              name="name"
              className="w-full bg-white"
              label={t('COPT_TARGET_RATED_POWER_GROUPS_NAME')}
              onChange={(value: string) => {
                setUpdatedTargetRatedPowerGroup({
                  ...updatedTargetRatedPowerGroup,
                  name: value,
                });
              }}
              disabled={readOnly('target_rated_power_groups')}
              endAdornment={<InfoTooltip field="name" objectType="target_rated_power_groups" />}
            />
          </div>
        )}
        {updatedTargetRatedPowerGroup.target_rated_power_eeg !== undefined && (
          <NumberInput
            value={
              updatedTargetRatedPowerGroup.target_rated_power_eeg &&
              updatedTargetRatedPowerGroup.target_rated_power_eeg * 1000
            }
            className="w-full bg-white"
            name="target_rated_power_eeg"
            label={t('COPT_TARGET_RATED_POWER_GROUPS_TARGET_RATED_POWER_EEG')}
            onChange={(value: Nullable<number>) => {
              setUpdatedTargetRatedPowerGroup({
                ...updatedTargetRatedPowerGroup,
                target_rated_power_eeg: value != null && !Number.isNaN(value) ? value / 1000 : null,
              });
            }}
            disabled={readOnly('target_rated_power_groups')}
            rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
            endAdornment={<InfoTooltip field="target_rated_power_eeg" objectType="target_rated_power_groups" />}
          />
        )}
        {updatedTargetRatedPowerGroup.eeg_premium !== undefined && (
          <NumberInput
            value={updatedTargetRatedPowerGroup.eeg_premium}
            className="w-full bg-white"
            name="eeg_premium"
            label={t('COPT_TARGET_RATED_POWER_GROUPS_EEG_PREMIUM')}
            onChange={(value: Nullable<number>) => {
              setUpdatedTargetRatedPowerGroup({
                ...updatedTargetRatedPowerGroup,
                eeg_premium: value != null && !Number.isNaN(value) ? value : null,
              });
            }}
            disabled={readOnly('target_rated_power_groups')}
            notNegative
            endAdornment={<InfoTooltip field="eeg_premium" objectType="target_rated_power_groups" />}
          />
        )}
        <NumberInput
          value={
            updatedTargetRatedPowerGroup.schedule_min_mw === null
              ? null
              : updatedTargetRatedPowerGroup.schedule_min_mw * 1000
          }
          className="w-full bg-white"
          name="schedule_min_mw"
          label={t('COPT_TARGET_RATED_POWER_GROUPS_SCHEDULE_MIN_MW')}
          onChange={(value: Nullable<number>) => {
            setUpdatedTargetRatedPowerGroup({
              ...updatedTargetRatedPowerGroup,
              schedule_min_mw: value != null && !Number.isNaN(value) ? value / 1000 : null,
            });
          }}
          disabled={readOnly('schedule_min_mw')}
          rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
          endAdornment={<InfoTooltip field="schedule_min_mw" objectType="target_rated_power_groups" />}
          required={
            updatedTargetRatedPowerGroup.eighty_five_percent_rule_config !== null ||
            updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config !== null
          }
        />
        <NumberInput
          value={
            updatedTargetRatedPowerGroup.schedule_max_mw === null
              ? null
              : updatedTargetRatedPowerGroup.schedule_max_mw * 1000
          }
          className="w-full bg-white"
          name="schedule_max_mw"
          label={t('COPT_TARGET_RATED_POWER_GROUPS_SCHEDULE_MAX_MW')}
          onChange={(value: Nullable<number>) => {
            setUpdatedTargetRatedPowerGroup({
              ...updatedTargetRatedPowerGroup,
              schedule_max_mw: value != null && !Number.isNaN(value) ? value / 1000 : null,
            });
          }}
          disabled={readOnly('schedule_max_mw')}
          rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
          endAdornment={<InfoTooltip field="schedule_max_mw" objectType="target_rated_power_groups" />}
        />
        {updatedTargetRatedPowerGroup.eeg_regime !== undefined && (
          <FormControl
            className={clsx('w-full bg-white', {
              'xl:col-span-2': updatedTargetRatedPowerGroup.eeg_regime !== EEGRegime.EEG2023v2,
            })}
          >
            <InputLabel id="eeg_regime">{t('COPT_TARGET_RATED_POWER_GROUPS_EEG_REGIME')}</InputLabel>
            <Select
              id="eeg_regime-select"
              value={updatedTargetRatedPowerGroup.eeg_regime}
              label={t('COPT_TARGET_RATED_POWER_GROUPS_EEG_REGIME')}
              onChange={(e) => {
                //currently cOpt the one_third_quarter_hour_rule_config for EEG 2023 v2 is mandatory
                //the eighty_five_percent_rule_config isn't supported yet
                if (e.target.value === EEGRegime.EEG2023v2) {
                  setUpdatedTargetRatedPowerGroup({
                    ...updatedTargetRatedPowerGroup,
                    eeg_regime: e.target.value as EEGRegime,
                    eeg_2023_v2_threshold_ct_per_kwh: 2,
                    eighty_five_percent_rule_config: null,
                    one_third_quarter_hour_rule_config: {
                      n_target_quarter_hours: 11680,
                      initial_qh_count: null,
                    },
                  });
                } else {
                  setUpdatedTargetRatedPowerGroup({
                    ...updatedTargetRatedPowerGroup,
                    eeg_regime: e.target.value as EEGRegime,
                    // we need to be sure no rules are implemented for other regimes than EEG 2023 v2
                    eighty_five_percent_rule_config: null,
                    one_third_quarter_hour_rule_config: null,
                  });
                }
              }}
              disabled={readOnly('target_rated_power_groups')}
              endAdornment={
                <InfoTooltip
                  iconClass="pr-4 w-4 sm:text-green-200"
                  field="eeg_regime"
                  objectType="target_rated_power_groups"
                  title={t('COPT_TARGET_RATED_POWER_GROUPS_EEG_REGIME')}
                  text={
                    <Trans components={{ p: <p />, ul: <ul />, li: <li /> }}>
                      COPT_TARGET_RATED_POWER_GROUPS_EEG_REGIME_TOOLTIP
                    </Trans>
                  }
                />
              }
            >
              {Object.entries(EEGRegime).map((element) => (
                <MenuItem key={element[0]} value={element[1]}>
                  {t(`COPT_TARGET_RATED_POWER_GROUPS_EEG_REGIME_${element[1].toUpperCase()}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        {updatedTargetRatedPowerGroup.eeg_2023_v2_threshold_ct_per_kwh !== undefined &&
          updatedTargetRatedPowerGroup.eeg_regime === EEGRegime.EEG2023v2 && (
            <>
              <NumberInput
                value={updatedTargetRatedPowerGroup.eeg_2023_v2_threshold_ct_per_kwh}
                className="w-full bg-white"
                name="eeg_premium"
                label={t('COPT_TARGET_RATED_POWER_GROUPS_EEG_2023_V2_THRESHOLD_CT_PER_KWH')}
                onChange={(value: Nullable<number>) => {
                  setUpdatedTargetRatedPowerGroup({
                    ...updatedTargetRatedPowerGroup,
                    eeg_2023_v2_threshold_ct_per_kwh: value != null && !Number.isNaN(value) ? value : null,
                  });
                }}
                disabled={readOnly('target_rated_power_groups')}
                notNegative
                endAdornment={
                  <InfoTooltip field="eeg_2023_v2_threshold_ct_per_kwh" objectType="target_rated_power_groups" />
                }
              />

              <h4 className="m-0 flex items-center text-sm font-semibold xl:col-span-2">
                {t('COPT_TARGET_RATED_POWER_GROUPS_ONE_THIRD_QUARTER_HOUR_RULE')}
                <InfoTooltip
                  text={t('COPT_TARGET_RATED_POWER_GROUPS_ONE_THIRD_QUARTER_HOUR_RULE_TOOLTIP')}
                  field="one_third_quarter_hour_rule_config"
                />
              </h4>
              <NumberInput
                value={updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.n_target_quarter_hours ?? 11680}
                className="w-full bg-white"
                name="n_target_quarter_hours"
                label={t('COPT_TARGET_RATED_POWER_GROUPS_EEG_2023_V2_N_TARGET_QUARTER_HOURS')}
                onChange={(value: Nullable<number>) => {
                  setUpdatedTargetRatedPowerGroup({
                    ...updatedTargetRatedPowerGroup,
                    one_third_quarter_hour_rule_config: {
                      n_target_quarter_hours: value != null && !Number.isNaN(value) ? value : 11680,
                      initial_qh_count:
                        updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.initial_qh_count ?? null,
                    },
                  });
                }}
                disabled={readOnly('target_rated_power_groups')}
                rules={[mustBeGreaterThan(0, tv('COMMON_MUST_BE_GREATER_THAN', { value: 0 }))]}
                endAdornment={
                  <InfoTooltip field="eeg_2023_v2_n_target_quarter_hours" objectType="target_rated_power_groups" />
                }
              />
              <NumberInput
                value={updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.initial_qh_count?.count ?? 0}
                className="w-full bg-white"
                name="initial_qh_count.count"
                label={t('COPT_TARGET_RATED_POWER_GROUPS_EEG_2023_V2_INITIAL_QH_COUNT')}
                required
                onChange={(value: number) => {
                  const initial_qh_count = {
                    count: value ?? 0,
                    timestamp:
                      updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.initial_qh_count?.timestamp ??
                      new Date().toISOString(),
                  };
                  setUpdatedTargetRatedPowerGroup({
                    ...updatedTargetRatedPowerGroup,
                    one_third_quarter_hour_rule_config: {
                      n_target_quarter_hours:
                        updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.n_target_quarter_hours ??
                        11680,
                      initial_qh_count,
                    },
                  });
                }}
                disabled={readOnly('target_rated_power_groups')}
                notNegative
                endAdornment={
                  <InfoTooltip field="eeg_2023_v2_initial_qh_count" objectType="target_rated_power_groups" />
                }
              />
              <DateTimePicker
                label={t('COPT_TARGET_RATED_POWER_GROUPS_EEG_2023_V2_INITIAL_QH_COUNT_TIMESTAMP')}
                mode="single"
                required
                pickerProps={{
                  disabled: {
                    after: new Date(),
                  },
                  numberOfMonths: 1,
                  fixedWeeks: true,
                  showWeekNumber: true,
                  showOutsideDays: true,
                  locale,
                  startOfWeek,
                }}
                timePickerProps={{
                  from: {
                    minutesStep: 1,
                  },
                }}
                textFieldProps={{
                  InputProps: {
                    endAdornment: (
                      <Button
                        type="button"
                        className="h-8 min-w-0 p-x-2"
                        onClick={(event) => {
                          event.stopPropagation();
                          const initial_qh_count = {
                            count:
                              updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.initial_qh_count
                                ?.count ?? 0,
                            timestamp: new Date().toISOString(),
                          };
                          setUpdatedTargetRatedPowerGroup({
                            ...updatedTargetRatedPowerGroup,
                            one_third_quarter_hour_rule_config: {
                              n_target_quarter_hours:
                                updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config
                                  ?.n_target_quarter_hours ?? 11680,
                              initial_qh_count: initial_qh_count,
                            },
                          });
                        }}
                      >
                        {t('SHARED_NOW')}
                      </Button>
                    ),
                  },
                }}
                value={
                  updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.initial_qh_count?.timestamp
                    ? parseISO(
                        updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.initial_qh_count?.timestamp,
                      )
                    : undefined
                }
                onSelect={() => {
                  const initial_qh_count = {
                    count:
                      updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.initial_qh_count?.count ?? 0,
                    timestamp: new Date().toISOString(),
                  };
                  setUpdatedTargetRatedPowerGroup({
                    ...updatedTargetRatedPowerGroup,
                    one_third_quarter_hour_rule_config: {
                      n_target_quarter_hours:
                        updatedTargetRatedPowerGroup.one_third_quarter_hour_rule_config?.n_target_quarter_hours ??
                        11680,
                      initial_qh_count: initial_qh_count,
                    },
                  });
                }}
              />
            </>
          )}
        {updatedTargetRatedPowerGroup.penalty_lower !== undefined && (
          <div className="col-start-1">
            <NumberInput
              value={updatedTargetRatedPowerGroup.penalty_lower}
              className="w-full bg-white"
              name="penalty_lower"
              label={t('COPT_TARGET_RATED_POWER_GROUPS_PENALTY_LOWER')}
              onChange={(value: Nullable<number>) => {
                setUpdatedTargetRatedPowerGroup({
                  ...updatedTargetRatedPowerGroup,
                  penalty_lower: value != null && !Number.isNaN(value) ? value : null,
                });
              }}
              disabled={readOnly('target_rated_power_groups')}
              notNegative
              endAdornment={<InfoTooltip field="penalty_lower" objectType="target_rated_power_groups" />}
            />
          </div>
        )}
        {updatedTargetRatedPowerGroup.penalty_upper !== undefined && (
          <NumberInput
            value={updatedTargetRatedPowerGroup.penalty_upper}
            className="w-full bg-white"
            name="penalty_upper"
            label={t('COPT_TARGET_RATED_POWER_GROUPS_PENALTY_UPPER')}
            onChange={(value: Nullable<number>) => {
              setUpdatedTargetRatedPowerGroup({
                ...updatedTargetRatedPowerGroup,
                penalty_upper: value != null && !Number.isNaN(value) ? value : null,
              });
            }}
            disabled={readOnly('target_rated_power_groups')}
            notNegative
            endAdornment={<InfoTooltip field="penalty_upper" objectType="target_rated_power_groups" />}
          />
        )}
      </ValidatedContainer>
    </div>
  );
}
export const TargetRatedPowerGroup = memo(TargetRatedPowerGroupMemo);
